import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import {
  ArrowBack as ArrowBackIcon,
  CameraAlt as CameraAltIcon,
  Cameraswitch as CameraswitchIcon,
  CheckCircle as CheckCircleIcon,
  Print as PrintIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material/';

import { AppContext } from '../../../context/Context';
import { SocketContext } from '../../../context/SocketContext';
import { useTranslation } from 'react-i18next';
import '../../../assets/css/buttons.css';
import TouchButton from '../../../components/TouchButton/TouchButton';
import {
  centeredLabelSx,
  verticallyCenteredLabelSx,
} from '../../../theme/fieldTokens';
import toast from 'react-hot-toast';

// Matches the kiosk screens next to it, so switching tiles does not resize the modal
const TABLE_HEIGHT = '46vh';
const SKELETON_ROWS = 8;

// NT lodd stock: kiosk screen with the sold column replaced by typed adjustments
function ModalNtLoddStock({ onBack }) {
  const { t } = useTranslation();
  const { socket } = useContext(SocketContext);
  const { hall, operator, session, printer } = useContext(AppContext);

  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  // Which lodd is being moved, in which direction, and by how many
  const [editing, setEditing] = useState(null);
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');

  // Delivery note for what arrived; only the 'in' direction has one
  const [captured, setCaptured] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [printing, setPrinting] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [devices, setDevices] = useState([]);
  const [cameraIndex, setCameraIndex] = useState(0);

  const isFilling = editing?.mode === 'in';

  const closeEditor = () => {
    setEditing(null);
    setQty('');
    setReason('');
    setCaptured(null);
    setPhotoUrl(null);
    // Reset busy so an in-flight upload doesn't leave confirm disabled next time
    setBusy(false);
  };

  useEffect(() => {
    if (!socket) return;

    const handleState = (response) => {
      if (!response || response.ok === false) {
        setFailed(true);
        return;
      }
      setFailed(false);
      setData(response);
    };

    // Keep the current list when the attached state failed to load
    const refresh = (response) => {
      if (response?.items) handleState(response);
    };

    const reasons = {
      qtyRequired: 'sales.ntLoddQtyRequired',
      reasonRequired: 'sales.cashMovementReasonRequired',
      photoRequired: 'sales.ntLoddPhotoRequired',
    };

    const handleMove = (response) => {
      setBusy(false);
      if (!response || response.ok === false) {
        toast.error(t(reasons[response?.reason] || 'error.error'));
        return;
      }
      toast.success(t('sales.ntLoddDone'));
      closeEditor();
      // Write confirmed even if the re-read failed; don't blank the list
      refresh(response);
    };

    const handlePrint = (response) => {
      setPrinting(false);
      if (!response) {
        toast.error(t('sales.ntLoddPrintFailed'));
        return;
      }
      toast.success(t('sales.ntLoddPrintDone'));
    };

    socket.on('ntLoddStockState', handleState);
    socket.on('ntLoddStockMove', handleMove);
    socket.on('printNtLoddStock', handlePrint);
    socket.emit('ntLoddStockState', { bingoName: hall });

    return () => {
      socket.off('ntLoddStockState', handleState);
      socket.off('ntLoddStockMove', handleMove);
      socket.off('printNtLoddStock', handlePrint);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, hall]);

  // Fill view only: a lingering listener would catch another screen's upload
  useEffect(() => {
    if (!socket || !isFilling) return;

    const handleUpload = (response) => {
      setBusy(false);
      if (!response?.url) {
        toast.error(t('errorTicket.photoFailed'));
        return;
      }
      setPhotoUrl(response.url);
      toast.success(t('errorTicket.photoAttached'));
    };

    socket.on('uploadImageSignature', handleUpload);
    return () => socket.off('uploadImageSignature', handleUpload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, isFilling]);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  // A live preview, same as ModalKioskStock — never a file picker
  useEffect(() => {
    if (!isFilling || captured) {
      stopCamera();
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const first = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        // Labels only arrive once permission is granted, so enumerate after asking
        const all = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = all.filter((d) => d.kind === 'videoinput');

        if (cancelled) {
          first.getTracks().forEach((track) => track.stop());
          return;
        }
        setDevices(videoDevices);

        // The first stream is whatever the browser picked; swap if another was chosen
        let live = first;
        const wanted = videoDevices[cameraIndex]?.deviceId;
        if (wanted && cameraIndex > 0) {
          first.getTracks().forEach((track) => track.stop());
          live = await navigator.mediaDevices.getUserMedia({
            video: { deviceId: { exact: wanted } },
          });
        }

        streamRef.current = live;
        if (videoRef.current) {
          videoRef.current.srcObject = live;
          videoRef.current.play();
        }
      } catch (err) {
        console.error('Error accessing the camera:', err);
        toast.error(t('errorTicket.cameraFailed'));
      }
    })();

    return () => {
      cancelled = true;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilling, captured, cameraIndex]);

  // A modal closed mid-shot must not leave the camera light on
  useEffect(() => stopCamera, []);

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    setCaptured(canvas.toDataURL('image/png'));
  };

  // Same channel the signature photos use, so pictures live in one place
  const handleUsePhoto = () => {
    if (!captured) return;

    const binary = atob(captured.split(',')[1]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

    setBusy(true);
    socket.emit('uploadImageSignature', {
      fileBuffer: bytes,
      fileName: `ntlodd_${hall}_${Date.now()}.png`,
      bingoName: hall,
      operator,
      session,
    });
  };

  const submit = () => {
    const value = Number(qty);
    if (qty === '' || !Number.isFinite(value) || value <= 0) {
      toast.error(t('sales.ntLoddQtyRequired'));
      return;
    }

    if (isFilling) {
      // A picture taken but never attached is the easiest thing to lose here
      if (!photoUrl) {
        toast.error(t('sales.ntLoddPhotoRequired'));
        return;
      }
    } else if (!reason.trim()) {
      toast.error(t('sales.cashMovementReasonRequired'));
      return;
    }

    setBusy(true);
    socket.emit('ntLoddStockMove', {
      bingoName: hall,
      operator,
      loddID: editing.item.loddID,
      // Any outgoing lodd is a negative adjustment; the reason tells them apart
      type: isFilling ? 'in' : 'adjust',
      qty: isFilling ? value : -value,
      reason: isFilling ? null : reason.trim(),
      photoUrl: isFilling ? photoUrl : null,
    });
  };

  const headerCellSx = {
    color: '#8fa3d4 !important',
    fontSize: '17px',
    fontWeight: 700,
    backgroundColor: '#151d3b',
  };
  const bodyCellSx = { color: '#e6eaf2 !important', fontSize: '19px' };
  const numberSx = { ...bodyCellSx, fontVariantNumeric: 'tabular-nums' };
  const rowButtonSx = {
    minWidth: 118,
    height: 52,
    fontSize: 16,
    px: 1,
  };
  const tableSx = {
    height: TABLE_HEIGHT,
    overflowY: 'auto',
    backgroundColor: '#151d3b',
    borderRadius: '14px',
    border: '1px solid #2a3a6e',
  };

  const items = data?.items || [];

  const printStock = () => {
    setPrinting(true);
    socket.emit('printNtLoddStock', {
      bingoName: hall,
      operator: operator,
      sessionID: session,
      printer: printer,
      action: 'printNtLoddStock',
    });
  };

  const renderEditor = () => (
    <Box sx={{ mt: 1, mb: 1, textAlign: 'center' }}>
      <Typography sx={{ color: '#8fa3d4', fontSize: '17px' }}>
        {isFilling ? t('sales.ntLoddFill') : t('sales.ntLoddRemove')}
      </Typography>
      <Typography sx={{ color: '#e6eaf2', fontSize: '24px' }}>
        {editing.item.name}
      </Typography>
      <Typography sx={{ color: '#8fa3d4', fontSize: '17px', mb: 2 }}>
        {t('sales.ntLoddCurrent', { count: editing.item.stock })}
      </Typography>

      {/* Not type="number": that draws the native spinner. inputMode keeps the
          numeric keypad, and the digits are already filtered on the way in */}
      <TextField
        autoFocus
        autoComplete="off"
        label={t('sales.ntLoddQty') + '*'}
        value={qty}
        onChange={(e) => setQty(e.target.value.replace(/[^0-9]/g, ''))}
        variant="outlined"
        className="modal-textfield"
        // Unshrunk the label is the placeholder, so it sits over the centred digits
        InputLabelProps={{ sx: centeredLabelSx }}
        inputProps={{
          inputMode: 'numeric',
          style: { textAlign: 'center', fontSize: '28px' },
        }}
        style={{ width: '260px' }}
      />

      {isFilling ? (
        <>
          <Typography sx={{ color: '#8fa3d4', fontSize: '15px', mt: 2, mb: 1 }}>
            {t('sales.ntLoddPhotoHelp')}
          </Typography>

          <Box
            sx={{
              borderRadius: '10px',
              overflow: 'hidden',
              backgroundColor: '#0b1229',
              height: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {captured ? (
              <img
                src={captured}
                alt=""
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <video
                ref={videoRef}
                muted
                playsInline
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            )}
          </Box>

          <Box
            sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'center' }}
          >
            {captured ? (
              <>
                <button
                  className="btn-custom-camara"
                  onClick={handleUsePhoto}
                  disabled={busy || Boolean(photoUrl)}
                >
                  <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                  {photoUrl
                    ? t('errorTicket.photoAttached')
                    : t('errorTicket.photoUse')}
                </button>
                <button
                  className="btn-custom-camara-back"
                  onClick={() => {
                    setCaptured(null);
                    setPhotoUrl(null);
                  }}
                >
                  <ReplayIcon style={{ fontSize: '1.2em' }} />
                  {t('errorTicket.photoRetake')}
                </button>
              </>
            ) : (
              <>
                <button className="btn-custom-camara" onClick={handleCapture}>
                  <CameraAltIcon style={{ fontSize: '1.2em' }} />
                  {t('errorTicket.photoTake')}
                </button>
                {devices.length > 1 && (
                  <button
                    className="btn-custom-camara-cancel"
                    onClick={() =>
                      setCameraIndex((i) => (i + 1) % devices.length)
                    }
                  >
                    <CameraswitchIcon style={{ fontSize: '1.2em' }} />
                    {t('errorTicket.photoSwitch')}
                  </button>
                )}
              </>
            )}
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ mt: 2 }}>
            <TextField
              autoComplete="off"
              label={t('sales.cashMovementReason') + '*'}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              variant="outlined"
              className="modal-textfield"
              // Left-aligned value, so the label only needs vertical centring
              InputLabelProps={{ sx: verticallyCenteredLabelSx }}
              inputProps={{ maxLength: 500, style: { fontSize: '22px' } }}
              style={{ width: '420px' }}
            />
          </Box>
          <Typography sx={{ color: '#8fa3d4', fontSize: '15px', mt: 1.5 }}>
            {t('sales.ntLoddRemoveHelp')}
          </Typography>
        </>
      )}

      <div className="react-confirm-alert-button-group">
        <button
          className="btn-custom-yes"
          onClick={submit}
          // Blocked until the delivery note is uploaded
          disabled={busy || (isFilling && !photoUrl)}
        >
          {t('sales.confirm')}
        </button>
        <button className="btn-custom-back" onClick={closeEditor}>
          <ArrowBackIcon />
          {t('sales.openShiftBack')}
        </button>
      </div>
    </Box>
  );

  const renderList = () => (
    <>
      <TableContainer sx={tableSx}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>{t('sales.ntLoddName')}</TableCell>
              <TableCell align="right" sx={headerCellSx}>
                {t('sales.ntLoddReceived')}
              </TableCell>
              <TableCell align="right" sx={headerCellSx}>
                {t('sales.ntLoddAdjusted')}
              </TableCell>
              <TableCell align="right" sx={headerCellSx}>
                {t('sales.ntLoddOnHand')}
              </TableCell>
              <TableCell align="right" sx={headerCellSx}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {failed && (
              <TableRow>
                <TableCell colSpan={5} sx={bodyCellSx}>
                  {t('error.error')}
                </TableCell>
              </TableRow>
            )}
            {!failed &&
              !data &&
              Array.from(new Array(SKELETON_ROWS)).map((_, row) => (
                <TableRow key={row}>
                  {[0, 1, 2, 3, 4].map((col) => (
                    <TableCell key={col} align={col === 0 ? 'left' : 'right'}>
                      <Skeleton
                        variant="text"
                        height={30}
                        width={col === 0 ? '60%' : '40%'}
                        sx={{
                          bgcolor: '#232a45',
                          ml: col === 0 ? 0 : 'auto',
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {!failed && data && !items.length && (
              <TableRow>
                <TableCell colSpan={5} sx={bodyCellSx}>
                  {t('sales.ntLoddEmpty')}
                </TableCell>
              </TableRow>
            )}
            {items.map((item) => (
              <TableRow key={item.loddID}>
                <TableCell sx={bodyCellSx}>
                  {item.name}
                  {/* Never stocked here: the figure is not a zero, it is unknown */}
                  {item.received === 0 && item.adjusted === 0 && (
                    <Typography sx={{ color: '#8fa3d4', fontSize: '14px' }}>
                      {t('sales.ntLoddUntracked')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={numberSx}>
                  {item.received}
                </TableCell>
                {/* Signed on purpose: a correction upwards moves the balance too,
                    and netting it into the column above would hide it */}
                <TableCell align="right" sx={numberSx}>
                  {item.adjusted > 0 ? `+${item.adjusted}` : item.adjusted}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    ...numberSx,
                    fontWeight: 700,
                    color:
                      item.stock < 0 ? '#fcad89 !important' : bodyCellSx.color,
                  }}
                >
                  {item.stock}
                </TableCell>
                <TableCell align="right">
                  {/* Row-sized like the other table screens, not a menu tile */}
                  <Box
                    sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}
                  >
                    <TouchButton
                      tone="confirm"
                      sx={rowButtonSx}
                      onClick={() => {
                        closeEditor();
                        setEditing({ item, mode: 'in' });
                      }}
                    >
                      {t('sales.ntLoddFill')}
                    </TouchButton>
                    {/* Amber, the colour goods-leaving already has elsewhere */}
                    <TouchButton
                      tone="back"
                      sx={rowButtonSx}
                      onClick={() => {
                        closeEditor();
                        setEditing({ item, mode: 'out' });
                      }}
                    >
                      {t('sales.ntLoddRemove')}
                    </TouchButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography sx={{ color: '#8fa3d4', fontSize: '15px', mt: 1.5 }}>
        {t('sales.ntLoddHelp')}
      </Typography>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 12,
          marginTop: 12,
        }}
      >
        <button className="btn-custom-no" onClick={onBack}>
          <ArrowBackIcon />
          {t('sales.openShiftBack')}
        </button>
        {/* Default TouchButton sizing: 90px, the height btn-custom-* also has */}
        <TouchButton
          tone="info"
          startIcon={<PrintIcon />}
          disabled={printing || !data || !items.length}
          onClick={printStock}
        >
          {t('sales.ntLoddPrint')}
        </TouchButton>
      </div>
    </>
  );

  return (
    <>
      <h1>{t('sales.ntLoddTitle')}</h1>

      {editing ? renderEditor() : renderList()}
    </>
  );
}

export default ModalNtLoddStock;
